import Cookies from "js-cookie";

import { generateIdempotencyKey } from "../../helpers/generateIdempotencyKey";

// PAY-404 (Slice 1.2) -- the pre-payment funnel events cannot anchor on the
// checkout attempt ID (doesn't exist yet) or session ID (created by a
// separate post-render request), so they dedup on a client-minted funnel
// UUID held in a first-party cookie.
//
// Mint-or-reuse keys off whether this page load is a *return from a
// required-payment redirect* (3DS/SEPA/iDEAL/Bancontact) -- never off the
// cookie's mere presence or a paywall-id match. Any other (fresh/organic)
// mount always mints fresh, even to the same paywall.
const FUNNEL_UUID_COOKIE = "_circle_ct_funnel_uuid";

// Same query params `PaywallCheckoutAttemptsStageHandler` and
// `useVerifyAttempt`'s `resetURL` already check on mount, and
// `CheckoutController#verifying_purchase?` checks server-side.
const REDIRECT_RETURN_PARAMS = [
  "payment_intent",
  "invoice",
  "setup_intent",
  "redirect_status",
  "payment_method_type",
];

// Exported so any FE call site that needs to know whether the current mount
// is a genuine redirect return (rather than re-deriving the check) can reuse
// it -- e.g. gating `add_payment_info` from re-firing on a post-redirect page
// load, the same way `begin_checkout` is gated via `wasSessionFetched`.
export const isRedirectReturn = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return REDIRECT_RETURN_PARAMS.some(param => params.has(param));
};

export const mintFunnelUuid = (): string => generateIdempotencyKey();

/**
 * Selects the funnel UUID for a fresh mount or a required-payment redirect
 * return. Persistence is handled separately so the caller can wait for consent.
 */
export const resolveFunnelUuid = (
  search: string = globalThis.location?.search ?? "",
): string => {
  const existing = Cookies.get(FUNNEL_UUID_COOKIE);

  if (isRedirectReturn(search) && existing) {
    return existing;
  }

  return mintFunnelUuid();
};

export const persistFunnelUuid = (uuid: string) =>
  Cookies.set(FUNNEL_UUID_COOKIE, uuid, { sameSite: "lax" });

export const clearFunnelUuid = () => Cookies.remove(FUNNEL_UUID_COOKIE);
